import { useEffect, useRef, useState } from "react";
import { ImageOff } from "lucide-react";

import type {
  AppliedFramingReceipt,
  FramingArtifact,
  FramingCoordinateSpace,
  FramingDecision,
  FramingDeliveredSubjectEvidence,
  FramingEvidenceMode,
  FramingObservation,
} from "./framingInspector";
import { FramingOverlay, type FramingOverlayLayer } from "./FramingOverlay";
import {
  displayDimensionsForSpace,
  intrinsicDimensionsMatch,
} from "./framingOverlayMath";
import { clampSceneRelativeTime } from "./framingViewUtils";

function isVideoArtifact(artifact: FramingArtifact | null): boolean {
  const contentType = artifact?.contentType?.toLowerCase() ?? "";
  const key = artifact?.key?.toLowerCase() ?? "";
  return contentType.startsWith("video/") || /\.(mp4|mov|webm)$/.test(key);
}

export function FramingStageViewer({
  label,
  coordinateSpace,
  coordinateSpaces,
  artifact,
  observations,
  deliveredEvidence = [],
  decision,
  receipt,
  evidenceMode,
  visibleLayers,
  timeSeconds,
  onTimeChange,
}: {
  label: string;
  coordinateSpace: FramingCoordinateSpace | null;
  coordinateSpaces: FramingCoordinateSpace[];
  artifact: FramingArtifact | null;
  observations: FramingObservation[];
  deliveredEvidence?: FramingDeliveredSubjectEvidence[];
  decision: FramingDecision | null;
  receipt: AppliedFramingReceipt | null;
  evidenceMode: FramingEvidenceMode;
  visibleLayers: ReadonlySet<FramingOverlayLayer>;
  timeSeconds: number;
  onTimeChange: (timeSeconds: number) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const video = isVideoArtifact(artifact);
  const [intrinsic, setIntrinsic] = useState<{
    key: string;
    width: number;
    height: number;
  } | null>(null);
  const mediaKey = `${coordinateSpace?.id ?? "no-space"}|${artifact?.url ?? "no-url"}`;
  const currentIntrinsic = intrinsic?.key === mediaKey ? intrinsic : null;
  const timelineOffset = coordinateSpace?.timelineStartSeconds ?? 0;
  const timelineEnd = coordinateSpace?.timelineEndSeconds;
  const timelineFps = receipt?.fps ?? 30;
  const artifactViewable =
    Boolean(artifact?.url) &&
    coordinateSpace?.retentionState !== "ephemeral" &&
    coordinateSpace?.viewable !== false;
  const expectedDisplay = displayDimensionsForSpace(
    coordinateSpace,
    coordinateSpaces,
  );
  const intrinsicMismatch =
    currentIntrinsic != null &&
    !intrinsicDimensionsMatch(
      currentIntrinsic.width,
      currentIntrinsic.height,
      expectedDisplay,
    );
  const overlayAllowed =
    artifactViewable && currentIntrinsic != null && !intrinsicMismatch;
  const unavailableMessage = !coordinateSpace
    ? "No coordinate-space record exists for this stage."
    : coordinateSpace.retentionState === "ephemeral"
      ? "These pixels were intentionally ephemeral. Exact geometry, dimensions, and hashes remain available, but there is no retained asset to display."
      : coordinateSpace.retentionState === "external"
        ? "This stage belongs to an external provider. Echoflicks retained the geometry receipt, not a viewable copy of the provider-internal pixels."
        : coordinateSpace.viewable === false
          ? "This coordinate space is metadata-only. It must not be presented as retained or viewable evidence."
          : coordinateSpace.retentionState === "retained" &&
              coordinateSpace.viewable === true
            ? "This space is marked retained and viewable, but no signed artifact matched its exact hash."
            : "Retention and viewability were not recorded for this coordinate space. Only its geometry record is available.";

  useEffect(() => {
    const element = videoRef.current;
    const targetTime = clampSceneRelativeTime(
      timeSeconds,
      timelineOffset,
      timelineEnd,
      timelineFps,
    ).absoluteSeconds;
    if (!element || Math.abs(element.currentTime - targetTime) < 0.2) return;
    element.currentTime = targetTime;
  }, [timeSeconds, timelineEnd, timelineFps, timelineOffset]);

  const handleVideoTime = (element: HTMLVideoElement) => {
    const clamped = clampSceneRelativeTime(
      element.currentTime - timelineOffset,
      timelineOffset,
      timelineEnd,
      timelineFps,
    );
    if (Math.abs(element.currentTime - clamped.absoluteSeconds) > 0.001) {
      element.currentTime = clamped.absoluteSeconds;
    }
    if (clamped.atEnd && !element.paused) element.pause();
    onTimeChange(clamped.relativeSeconds);
  };

  return (
    <div>
      <div className="relative aspect-video w-full overflow-hidden rounded-md border bg-black">
        {artifactViewable && artifact?.url ? (
          video ? (
            <video
              ref={videoRef}
              src={artifact.url}
              controls
              className="size-full object-contain"
              aria-label={`${label} framing evidence`}
              onLoadedMetadata={(event) => {
                setIntrinsic({
                  key: mediaKey,
                  width: event.currentTarget.videoWidth,
                  height: event.currentTarget.videoHeight,
                });
                event.currentTarget.currentTime = clampSceneRelativeTime(
                  timeSeconds,
                  timelineOffset,
                  timelineEnd,
                  timelineFps,
                ).absoluteSeconds;
              }}
              onSeeking={(event) => handleVideoTime(event.currentTarget)}
              onTimeUpdate={(event) => handleVideoTime(event.currentTarget)}
            />
          ) : (
            <img
              src={artifact.url}
              alt={`${label} framing evidence`}
              className="size-full object-contain"
              onLoad={(event) =>
                setIntrinsic({
                  key: mediaKey,
                  width: event.currentTarget.naturalWidth,
                  height: event.currentTarget.naturalHeight,
                })
              }
            />
          )
        ) : (
          <div className="flex size-full flex-col items-center justify-center gap-2 px-6 text-center text-sm text-white/70">
            <ImageOff className="size-6" />
            <span>{unavailableMessage}</span>
          </div>
        )}

        {overlayAllowed ? (
          <FramingOverlay
            coordinateSpace={coordinateSpace}
            coordinateSpaces={coordinateSpaces}
            observations={observations}
            deliveredEvidence={deliveredEvidence}
            evidenceMode={evidenceMode}
            decision={decision}
            receipt={receipt}
            timeSeconds={timeSeconds}
            visibleLayers={visibleLayers}
          />
        ) : null}

        {artifactViewable ? (
          <span className="pointer-events-none absolute bottom-2 left-2 rounded bg-black/75 px-2 py-1 text-[10px] text-white">
            Display: contain · blank bands are not a pipeline crop
          </span>
        ) : null}
        <span className="pointer-events-none absolute left-2 top-2 rounded bg-black/80 px-2 py-1 text-[10px] font-medium text-white">
          {evidenceMode === "proposed"
            ? "Overlay: proposed only; not applied"
            : evidenceMode === "actual"
              ? "Overlay: plan and applied receipt"
              : "Overlay application mode unknown"}
        </span>
        {expectedDisplay?.orientation !== "normal" ? (
          <span className="pointer-events-none absolute right-2 top-2 rounded bg-black/75 px-2 py-1 text-[10px] text-white">
            Display orientation:{" "}
            {expectedDisplay?.orientation.replaceAll("_", " ")}
          </span>
        ) : null}
      </div>
      {intrinsicMismatch ? (
        <div
          className="mt-2 rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-900 dark:bg-red-950/30 dark:text-red-100"
          role="alert"
        >
          Overlay hidden: retained coordinate-space dimensions are{" "}
          {expectedDisplay?.width} × {expectedDisplay?.height}, but this signed
          artifact decoded as {currentIntrinsic?.width} ×{" "}
          {currentIntrinsic?.height}. The pixels are not proven to belong to
          this coordinate space.
        </div>
      ) : null}
    </div>
  );
}
